import SharedNodesCluster from "./SharedNodesCluster";
import ComposedIterator from "../Auxiliar/Iterators/ComposedIterator";

export default class SharedNodesClustersGenerator {
    //Public:
    constructor(cParams) {
        this.saveParams(cParams);
        this.getAllSharedNumbers();
    }

    compute() {
        this.initSharedClusters();
        this.computeIndicesMatrix();
        this.moveCommonIndicesIntoCommonMatrix();
        this.createSharedClusters();
        return this._sharedClusters;
    }

    //Private:
    saveParams(cParams) {
        this._clusters = cParams.clusters;
        this._nodes = cParams.nodes;
    }

    getAllSharedNumbers() {
        let allSharedNumbers = [];
        const clusters = this._clusters;
        clusters.forEach(cluster => {
            const sharedNumbers = cluster.sharedNumbers;
            allSharedNumbers.push(sharedNumbers);
        });
        this._allSharedNumbers = allSharedNumbers;
    }

    initSharedClusters() {
        this._sharedClusters = [];
    }

    computeIndicesMatrix() {
        let indicesMatrix = [];
        const allSharedNumbers = this._allSharedNumbers;
        for (let i = 0; i < allSharedNumbers.length; i++) {
            const sharedNumbersA = allSharedNumbers[i];
            indicesMatrix[i] = [];
            for (let j = 0; j < allSharedNumbers.length; j++) {
                indicesMatrix[i][j] = [];
                const isUpperDiagonal = j > i;
                if (isUpperDiagonal) {
                    const sharedNumbersB = allSharedNumbers[j];
                    sharedNumbersA.forEach(number => {
                        const hasNumber = sharedNumbersB.indexOf(number) !== -1;
                        if (hasNumber) indicesMatrix[i][j].push(number);
                    });
                }
            }
        }
        this._indicesMatrix = indicesMatrix;
    }

    moveCommonIndicesIntoCommonMatrix() {
        this.initCommonMatrix();
        this.initMovedIndices();
        let indicesMatrix = this._indicesMatrix;
        for (let iRow = 0; iRow < indicesMatrix.length; iRow++) {
            const row = indicesMatrix[iRow];
            const cleanedRow = this.cleanArray(row);
            let sharedIndicesPerRow = this.computeSharedIndices(cleanedRow);
            this.moveRelationShipsToCommonMatrix(sharedIndicesPerRow, iRow);
        }
    }

    initCommonMatrix() {
        let commonMatrix = [];
        let indicesMatrix = this._indicesMatrix;
        for (let i = 0; i < indicesMatrix.length; i++) {
            commonMatrix[i] = [];
        }
        this._commonMatrix = commonMatrix;
    }

    initMovedIndices() {
        this._movedIndices = [];
    }

    computeSharedIndices(row) {
        let sharedIndicesPerRow = [];
        const iteratorOrder = row.length;
        let composedIterator = new ComposedIterator(iteratorOrder, row);
        composedIterator.setAsDiagonal();
        while (!composedIterator.hasFinished()) {
            const bannedIndices = composedIterator.getIndices();
            const array = this.removeBannedIndices(row, bannedIndices);
            const commonValues = this.getCommonValues(array);
            const arrayIsNotRepeated = !sharedIndicesPerRow.hasArray(commonValues);
            if (commonValues.isNotEmpty() && arrayIsNotRepeated) sharedIndicesPerRow.push(commonValues);
            composedIterator.next();
        }
        return sharedIndicesPerRow;
    }

    moveRelationShipsToCommonMatrix(sharedIndicesPerRow, iRow) {
        let commonMatrix = this._commonMatrix;
        sharedIndicesPerRow.forEach(array => {
            const cleanedArray = array.getWithoutValues(this._movedIndices);
            if (cleanedArray.isNotEmpty()) {
                commonMatrix[iRow].push(cleanedArray);
                this.removeCommonIndicesFromIndicesMatrix(cleanedArray);
                this._movedIndices.selfConcat(cleanedArray);
            }
        });
    }

    removeBannedIndices(oldArray, bannedIndices) {
        let newArray = [];
        oldArray.forEach((element, index) => {
            const itIsNotBanned = bannedIndices.indexOf(index) === -1;
            if (itIsNotBanned) newArray.push(element);
        });
        return newArray;
    }

    getCommonValues(arrayOfArrays) {
        let commonValues = [];
        const uniqueValues = this.getUniqueValues(arrayOfArrays);
        uniqueValues.forEach(value => {
            const valueIsInAll = this.isValueInAllCellArrays(value, arrayOfArrays);
            if (valueIsInAll) commonValues.push(value);
        });
        return commonValues;
    }

    getUniqueValues(arrayOfArrays) {
        const uniqueValues = [];
        arrayOfArrays.forEach(array => {
            array.forEach(element => {
                const elementIsNotInUniqueArray = uniqueValues.indexOf(element) === -1;
                if (elementIsNotInUniqueArray) uniqueValues.push(element);
            });
        });
        return uniqueValues;
    }

    isValueInAllCellArrays(value, arrayOfArrays) {
        let itIs = true;
        arrayOfArrays.forEach(array => {
            const valueIsNotInArray = array.indexOf(value) === -1;
            if (valueIsNotInArray) itIs = false;
        });
        return itIs;
    }

    cleanArray(array) {
        const A = this.removeEmptyCells(array);
        const B = this.removeDuplicates(A);
        const C = this.addInitialEmptyCell(B);
        return C;
    }

    createSharedClusters() {
        const indicesMatrix = this._indicesMatrix;
        const commonMatrix = this._commonMatrix;
        this.createSharedClustersFromMatrix(indicesMatrix);
        this.createSharedClustersFromMatrix(commonMatrix);
    }

    createSharedClustersFromMatrix(matrix) {
        matrix.forEach(row => {
            row.forEach(indices => {
                if (this.isNotEmpty(indices)) this.createSharedNodesCluster(indices);
            });
        });
    }

    createSharedNodesCluster(indices) {
        let nodes = this._nodes;
        let cluster = new SharedNodesCluster(nodes);
        cluster.compute(indices);
        this.addCluster(cluster);
    }

    removeCommonIndicesFromIndicesMatrix(indices) {
        let matrix = this._indicesMatrix;
        matrix.forEach(row => {
            row.forEach(array => {
                const numbers = Object.assign([], array);
                numbers.forEach(number => {
                    const shallRemoveNumber = indices.indexOf(number) !== -1;
                    if (shallRemoveNumber) {
                        const numberIndex = array.indexOf(number);
                        array.splice(numberIndex, 1);
                    }
                });
            });
        });
    }

    addCluster(sharedCluster) {
        this._sharedClusters.push(sharedCluster);
    }

    isNotEmpty(array) {
        if (array.length > 0) return true;
        else return false;
    }

    removeEmptyCells(arrayA) {
        let newArray = [];
        for (let i = 0; i < arrayA.length; i++) {
            const arrayB = arrayA[i];
            const itIsNotEmpty = arrayB.length > 0;
            if (itIsNotEmpty) newArray.push(arrayB);
        }
        return newArray;
    }

    addInitialEmptyCell(array) {
        let newArray = array.clone();
        newArray.unshift([]);
        return newArray;
    }

    removeDuplicates(arrayA) {
        let newArray = [];
        for (let i = 0; i < arrayA.length; i++) {
            const arrayB = arrayA[i];
            const itIsNotRepeated = !newArray.hasArray(arrayB);
            if (itIsNotRepeated) newArray.push(arrayB);
        }
        return newArray;
    }
}
